import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RecipeCard, RecipeCardSkeleton } from '../components/RecipeCard';
import { 
  ChevronRight, 
  Utensils, 
  Heart, 
  Leaf, 
  Clock, 
  Sparkles,
  ArrowRight,
  CheckCircle2,
  BookOpen,
  Lock,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MembershipDialog } from '../components/MembershipDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Obrázek Lucie
const LUCIA_IMAGE = "https://customer-assets.emergentagent.com/job_dd76ce3d-59ed-47fe-b5d2-d4834fd5fb74/artifacts/yczj4kjn_Generated%20Image%20October%2013%2C%202025%20-%202_21PM.png";

export default function HomePage() {
  const { t, user, language } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed recipes first if needed
        await fetch(`${API_URL}/api/seed-recipes`, { method: 'POST' });
        
        // Fetch recipes
        const response = await fetch(`${API_URL}/api/recipes?limit=8`);
        const data = await response.json();
        setRecipes(data.recipes || []);

        // Fetch favorites if logged in
        if (user) {
          const favResponse = await fetch(`${API_URL}/api/favorites`, {
            credentials: 'include'
          });
          if (favResponse.ok) {
            const favData = await favResponse.json();
            setFavorites(new Set(favData.favorites.map(f => f.recipe_id)));
          }
        }
      } catch (error) {
        console.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const toggleFavorite = async (recipeId) => {
    if (!user) return;

    try {
      if (favorites.has(recipeId)) {
        await fetch(`${API_URL}/api/favorites/${recipeId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(recipeId);
          return next;
        });
      } else {
        await fetch(`${API_URL}/api/favorites/${recipeId}`, {
          method: 'POST',
          credentials: 'include'
        });
        setFavorites(prev => new Set([...prev, recipeId]));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const magicFive = [
    { 
      icon: Utensils, 
      label: language === 'sk' ? 'Chutne' : 'Chutně', 
      color: 'bg-primary/10 text-primary',
      description: language === 'sk' ? 'Jedlo, ktoré chutí celej rodine' : 'Jídlo, které chutná celé rodině'
    },
    { 
      icon: Heart, 
      label: language === 'sk' ? 'Zdravo' : 'Zdravě', 
      color: 'bg-secondary/10 text-secondary',
      description: language === 'sk' ? 'Vyvážené a výživné recepty' : 'Vyvážené a výživné recepty'
    },
    { 
      icon: Clock, 
      label: language === 'sk' ? 'Rýchlo' : 'Rychle', 
      color: 'bg-accent-yellow/20 text-amber-700',
      description: language === 'sk' ? 'Hotové za 30 minút alebo menej' : 'Hotové za 30 minut nebo méně'
    },
    { 
      icon: Leaf, 
      label: language === 'sk' ? 'Dostupne' : 'Dostupně', 
      color: 'bg-secondary/10 text-secondary',
      description: language === 'sk' ? 'Z bežne dostupných surovín' : 'Z běžně dostupných surovin'
    },
    { 
      icon: Sparkles, 
      label: language === 'sk' ? 'Jednoducho' : 'Jednoduše', 
      color: 'bg-primary/10 text-primary',
      description: language === 'sk' ? 'Zvládne to každý' : 'Zvládne to každý'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-cream-subtle to-background py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Text content */}
            <div className="animate-slide-up order-2 lg:order-1">
              <span className="font-accent text-2xl md:text-3xl text-primary rotate-[-2deg] inline-block mb-4">
                {t('clubOfWomen')}
              </span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-text-heading leading-tight mb-6">
                S Luciou v kuchyni
                <span className="text-primary">.</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-lg">
                {language === 'sk' 
                  ? 'Vitajte v mojom svete, kde varíme s láskou a podľa Magickej päťky. Každý recept je vytvorený tak, aby bol CHUTNÝ, ZDRAVÝ, RÝCHLY, DOSTUPNÝ a JEDNODUCHÝ.'
                  : 'Vítejte v mém světě, kde vaříme s láskou a podle Magické pětky. Každý recept je vytvořen tak, aby byl CHUTNÝ, ZDRAVÝ, RYCHLÝ, DOSTUPNÝ a JEDNODUCHÝ.'
                }
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/recipes">
                  <Button 
                    size="lg" 
                    className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 btn-lift"
                    data-testid="get-started-btn"
                  >
                    {t('getStarted')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                </Link>
                <Link to="/ai-generator">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="rounded-full px-8 border-2 border-primary text-primary hover:bg-primary/5"
                    data-testid="ai-generator-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('aiGenerator')}
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero image - Lucia */}
            <div className="relative animate-fade-in stagger-2 order-1 lg:order-2">
              <div className="relative rounded-3xl overflow-hidden shadow-hover">
                <img
                  src={LUCIA_IMAGE}
                  alt="Lucia Súkeníková"
                  className="w-full h-[480px] md:h-[560px] lg:h-[600px] object-cover object-center"
                />
                {/* Subtle gradient only at bottom for text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />

                {/* Name overlay — bottom left */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h2 className="font-serif text-2xl md:text-3xl font-bold text-white drop-shadow">
                    Lucia Súkeníková
                  </h2>
                  <p className="font-accent text-lg text-white/90 drop-shadow">
                    {language === 'sk' ? 'kuchárka a autorka bestsellerov' : 'kuchařka a autorka bestsellerů'}
                  </p>
                </div>

                {/* Floating badge — top right corner, inside image */}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-hover hidden md:flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Heart className="w-5 h-5 text-primary fill-primary" />
                  </div>
                  <div>
                    <p className="font-serif font-semibold text-text-heading leading-tight">80 000+</p>
                    <p className="text-xs text-muted-foreground">
                      {language === 'sk' ? 'sledovateľov' : 'sledujících'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Magická päťka Section - Enhanced */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary" />
          <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-secondary" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12 animate-slide-up">
            <span className="font-accent text-2xl text-primary mb-2 block">
              {language === 'sk' ? 'Filozofia varenia' : 'Filozofie vaření'}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-text-heading mb-4">
              {t('magicFive')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {language === 'sk' 
                ? 'Päť pilierov, ktoré musí spĺňať každé jedlo v našej domácnosti. Podľa tejto filozofie vytváram všetky recepty.'
                : 'Pět pilířů, které musí splňovat každé jídlo v naší domácnosti. Podle této filozofie vytvářím všechny recepty.'
              }
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {magicFive.map((item, index) => (
              <div 
                key={item.label}
                className={`flex flex-col items-center p-6 rounded-2xl ${item.color} transition-all duration-300 hover:scale-105 hover:shadow-lg animate-scale-in group`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-8 h-8" />
                </div>
                <span className="font-serif text-lg font-semibold text-center mb-2">{item.label}</span>
                <span className="text-xs text-center opacity-80">{item.description}</span>
              </div>
            ))}
          </div>

          {/* Quote from Lucia */}
          <div className="mt-12 max-w-3xl mx-auto text-center">
            <blockquote className="relative">
              <span className="absolute -top-4 -left-2 text-6xl text-primary/20 font-serif">"</span>
              <p className="font-accent text-xl md:text-2xl text-text-heading italic px-8">
                {language === 'sk' 
                  ? 'Verím, že dobré jedlo nemusí byť komplikované. Keď varíte s láskou a podľa Magickej päťky, vytvoríte niečo výnimočné aj z jednoduchých surovín.'
                  : 'Věřím, že dobré jídlo nemusí být komplikované. Když vaříte s láskou a podle Magické pětky, vytvoříte něco výjimečného i z jednoduchých surovin.'
                }
              </p>
              <span className="absolute -bottom-4 -right-2 text-6xl text-primary/20 font-serif rotate-180">"</span>
            </blockquote>
            <p className="mt-6 font-accent text-lg text-primary">— Lucia</p>
          </div>
        </div>
      </section>

      {/* Featured Recipes Section */}
      <section className="py-16 bg-cream-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10 gap-4">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-text-heading">
                {t('recipes')}
              </h2>
              <p className="text-muted-foreground mt-2">
                {language === 'sk' 
                  ? 'Všetky recepty spĺňajú kritériá Magickej päťky'
                  : 'Všechny recepty splňují kritéria Magické pětky'
                }
              </p>
            </div>
            <Link to="/recipes">
              <Button variant="ghost" className="text-primary hover:text-primary-hover gap-1">
                {language === 'sk' ? 'Všetky recepty' : 'Všechny recepty'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <RecipeCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {recipes.map((recipe) => (
                <RecipeCard
                  key={recipe.recipe_id}
                  recipe={recipe}
                  isFavorite={favorites.has(recipe.recipe_id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Magic Five Works - New Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-accent text-xl text-primary mb-2 block">
                {language === 'sk' ? 'Prečo to funguje?' : 'Proč to funguje?'}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-text-heading mb-6">
                {language === 'sk' 
                  ? 'Magická päťka v praxi'
                  : 'Magická pětka v praxi'
                }
              </h2>
              <div className="space-y-4">
                {[
                  language === 'sk' 
                    ? 'Každý recept je testovaný v mojej domácej kuchyni'
                    : 'Každý recept je testovaný v mé domácí kuchyni',
                  language === 'sk'
                    ? 'Používam suroviny, ktoré nájdete v každom obchode'
                    : 'Používám suroviny, které najdete v každém obchodě',
                  language === 'sk'
                    ? 'Čas prípravy je reálny - nie kuchárska fantázia'
                    : 'Čas přípravy je reálný - ne kuchařská fantazie',
                  language === 'sk'
                    ? 'Jedlá chutia celej rodine, vrátane detí'
                    : 'Jídla chutnají celé rodině, včetně dětí',
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
                    <p className="font-serif text-3xl font-bold text-primary">100+</p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'sk' ? 'receptov' : 'receptů'}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
                    <p className="font-serif text-3xl font-bold text-secondary">30</p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'sk' ? 'minút max' : 'minut max'}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
                    <p className="font-serif text-3xl font-bold text-amber-600">5</p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'sk' ? 'pilierov' : 'pilířů'}
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
                    <p className="font-serif text-3xl font-bold text-text-heading">∞</p>
                    <p className="text-sm text-muted-foreground">
                      {language === 'sk' ? 'lásky' : 'lásky'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ebook Teaser Section - Members-only showcase */}
      <section className="py-16 bg-gradient-to-br from-cream-subtle via-white to-cream-subtle relative overflow-hidden" data-testid="ebook-teaser-section">
        {/* Decorative corner elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Heading */}
          <div className="text-center mb-12 animate-slide-up">
            <span className="font-accent text-2xl text-primary mb-2 block">
              {language === 'sk' ? 'Klenot členskej sekcie' : 'Klenot členské sekce'}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-text-heading mb-4">
              {language === 'sk'
                ? '13 ebookov s overenými receptami'
                : '13 ebooků s ověřenými recepty'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {language === 'sk'
                ? 'Sezónne kolekcie, tradičné slovenské jedlá, rýchle raňajky, pečenie & mnoho ďalšieho — všetko na jednom mieste pre členov.'
                : 'Sezónní kolekce, tradiční slovenská jídla, rychlé snídaně, pečení a mnoho dalšího — vše na jednom místě pro členy.'
              }
            </p>
          </div>

          {/* 3 featured ebook cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            {[
              {
                title_sk: 'Magická päťka — kompletný ebook',
                title_cz: 'Magická pětka — kompletní ebook',
                desc_sk: 'Komplexný sprievodca filozofiou varenia so všetkými 5 piliermi a desiatkami receptov.',
                desc_cz: 'Komplexní průvodce filozofií vaření se všemi 5 pilíři a desítkami receptů.',
                image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800',
                badge_sk: 'Bestseller',
                badge_cz: 'Bestseller',
                color: 'from-primary/20 to-primary/5',
              },
              {
                title_sk: 'Koláčový raj — dezerty & pečivo',
                title_cz: 'Koláčový ráj — dezerty & pečivo',
                desc_sk: 'Viac ako 30 receptov na tradičné aj moderné koláče, torty a sladké pokušenia.',
                desc_cz: 'Více než 30 receptů na tradiční i moderní koláče, dorty a sladká pokušení.',
                image: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=800',
                badge_sk: 'Najobľúbenejší',
                badge_cz: 'Nejoblíbenější',
                color: 'from-accent-yellow/30 to-amber-100',
              },
              {
                title_sk: 'Jarná receptová kolekcia',
                title_cz: 'Jarní receptová kolekce',
                desc_sk: 'Svieže jarné recepty s medvedím cesnakom, špargľou, bylinkami a sezónnou zeleninou.',
                desc_cz: 'Svěží jarní recepty s medvědím česnekem, chřestem, bylinkami a sezonní zeleninou.',
                image: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=800',
                badge_sk: 'Sezónne',
                badge_cz: 'Sezónní',
                color: 'from-secondary/20 to-secondary/5',
              },
            ].map((ebook, idx) => (
              <div
                key={idx}
                className={`group relative bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-500 hover:-translate-y-1 border border-border/40 animate-scale-in`}
                style={{ animationDelay: `${idx * 0.12}s` }}
                data-testid={`ebook-teaser-card-${idx}`}
              >
                {/* Cover image */}
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={ebook.image}
                    alt={language === 'sk' ? ebook.title_sk : ebook.title_cz}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  {/* Gradient overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t ${ebook.color} mix-blend-overlay`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                  {/* Lock overlay on hover if not member */}
                  {(!user || user.membership_type === 'free') && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/40 backdrop-blur-sm">
                      <div className="text-center px-6">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/90 flex items-center justify-center shadow-hover">
                          <Lock className="w-6 h-6 text-primary" />
                        </div>
                        <p className="font-accent text-lg text-white mb-1">
                          {language === 'sk' ? 'Len pre členov' : 'Jen pro členy'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Badge */}
                  <span className="absolute top-4 left-4 px-3 py-1 bg-white/95 backdrop-blur-sm rounded-full text-xs font-medium text-primary shadow-soft">
                    {language === 'sk' ? ebook.badge_sk : ebook.badge_cz}
                  </span>

                  {/* BookOpen icon top right */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-soft">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>

                  {/* Bottom text on image */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-serif text-xl md:text-2xl font-bold text-white drop-shadow-md leading-tight">
                      {language === 'sk' ? ebook.title_sk : ebook.title_cz}
                    </h3>
                  </div>
                </div>

                {/* Description */}
                <div className="p-5">
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
                    {language === 'sk' ? ebook.desc_sk : ebook.desc_cz}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Download className="w-3.5 h-3.5" />
                    <span>{language === 'sk' ? 'DOCX · stiahnutie zadarmo pre členov' : 'DOCX · stažení zdarma pro členy'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Unlock CTA */}
          <div className="text-center">
            {user && user.membership_type !== 'free' && user.membership_type !== 'expired' ? (
              <Link to="/member">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 btn-lift shadow-hover"
                  data-testid="ebook-teaser-cta-member"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  {language === 'sk' ? 'Otvoriť členskú sekciu' : 'Otevřít členskou sekci'}
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            ) : (
              <>
                <p className="text-muted-foreground mb-5 text-base md:text-lg">
                  {language === 'sk'
                    ? 'Získajte prístup ku všetkým 13 ebookom a 6-dielnemu videokurzu.'
                    : 'Získejte přístup ke všem 13 ebookům a 6dílnému videokurzu.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                  <MembershipDialog
                    trigger={
                      <Button
                        size="lg"
                        className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 btn-lift shadow-hover"
                        data-testid="ebook-teaser-cta-buy"
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        {language === 'sk' ? 'Kúpiť členstvo' : 'Koupit členství'}
                        <ChevronRight className="w-5 h-5 ml-1" />
                      </Button>
                    }
                  />
                  <Link to="/login" data-testid="ebook-teaser-cta-login">
                    <Button 
                      size="lg"
                      variant="outline"
                      className="rounded-full px-10 btn-lift border-2"
                    >
                      {language === 'sk' ? 'Prihlásiť sa' : 'Přihlásit se'}
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-6 opacity-80" />
          <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-4">
            {language === 'sk' ? 'Neviete, čo uvariť?' : 'Nevíte, co uvařit?'}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {language === 'sk'
              ? 'Nechajte sa inšpirovať našim AI asistentom. Stačí povedať, čo máte v chladničke, a navrhneme vám recept podľa Magickej päťky.'
              : 'Nechte se inspirovat naším AI asistentem. Stačí říct, co máte v lednici, a navrhneme vám recept podle Magické pětky.'
            }
          </p>
          <Link to="/ai-generator">
            <Button 
              size="lg" 
              variant="secondary"
              className="bg-white text-primary hover:bg-white/90 rounded-full px-8 btn-lift"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('aiGenerator')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
